import { Box, Tooltip, Typography } from '@mui/material';
import UPSnackBar from 'components/app-container/snackbar';
import BottomErrorBar from 'components/bottom-error-bar';
import BottomGreenBar from 'components/bottom-green-bar';
import CopyMultipleModal from 'components/copy-multiple-modal';
import DeleteModal from 'components/delete-modal';
import { UPDropdown, UPDropdownItem } from 'components/dropdown';
import UPDropdownWithSearch from 'components/dropdown-with-search';
import { FilterButton, StyledFilterIcon } from 'components/filters-menu/styles';
import UPLinkButton from 'components/link-button';
import MessageModal from 'components/message-modal';
import UPWeekPicker from 'components/week-picker';
import { CoefficientsPerWorkerClassOptions } from 'constants/CoefficientsPerWorkerclass';
import { CompanyAvailabilityFilterType } from 'constants/CompanyAvailabilityFilterType';
import { CompanyRole } from 'constants/CompanyRole';
import { ContractListViewTypes } from 'constants/ContractListViewTypes';
import { ContractPageSize } from 'constants/ContractPageSize';
import { ContractsPageViewType } from 'constants/ContractsPageViewType';
import { toNumber } from 'lodash';
import { CompanyAvailability } from 'modules/availabilities/company-availability';
import dayjs from 'utils/dayjs';
import { SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'store';
import {
  exportCompanyAvailabilityAction,
  requestAvailabilityFilteredAction,
} from 'store/actions/availabilities-actions';
import { getCompaniesAsDropdownItemsAction, getDefaultCompanyBreakTimeAction } from 'store/actions/company-actions';
import {
  clearCompanyAvailabilityFiltersAction,
  setCompanyAvailabilityFiltersAction,
} from 'store/actions/company-availability-filters-actions';
import { getCostCentersAction, getCostCentersDropdownItemsAction } from 'store/actions/company-cost-centers-actions';
import { getFunctionsAsDropdownItemsAction } from 'store/actions/company-functions-actions';
import { getLocationsDropdownItemsAction } from 'store/actions/company-locations-actions';
import { getVendorsAction } from 'store/actions/company-vendors-actions';
import {
  deleteContractsAction,
  directlyCardToCopyAction,
  downloadContractErrorsAction,
  getContractByIdAction,
  getContractCardsAction,
  getContractsInListViewAction,
  pasteMultipleContractsAction,
  pasteSingleContractAction,
  reopenConfirmationAction,
  setContractCardToCopyAction,
  setErrors,
  toggleCopyModeAction,
  tooManyContractsAction,
  validateContractErrorMessageAction,
  validateContractErrorMessageExtraInfoAction,
  validateContractErrorMessageRowAction,
  validateContractSuccessAction,
} from 'store/actions/contract-actions';
import { setBulkConfirmErrorsAction } from 'store/actions/contract-processing-actions';
import { getEmployeesDropdownItems } from 'store/actions/employee-actions';
import { setMenuStatusAction } from 'store/actions/menu-actions';
import { getAnalyticsService } from 'services/analytics';
import { trackFiltersChanged } from 'services/analytics/filtersTracking';
import { companyAvailabilityFiltersSelector } from 'store/selectors/company-availability-filters-selector';
import { costCentersDropdownItemsSelector } from 'store/selectors/company-cost-centers-selectors';
import { functionsDropdownItemsSelector } from 'store/selectors/company-functions-selectors';
import { locationsDropdownItemsSelector } from 'store/selectors/company-locations-selectors';
import { companiesDropdownItemsSelector, defaultCompanyBreakSelector } from 'store/selectors/company-selectors';
import { vendorsSelector } from 'store/selectors/company-vendors-selectors';
import {
  saveContractSuccessSelector,
  validateContractErrorMessageSelector,
  validateContractExtraMessageSelector,
} from 'store/selectors/contract-selectors';
import {
  contractCardToCopySelector,
  contractsLoadingSelector,
  deleteContractsSuccessActionSelector,
  getContractsErrors,
  isCopyModeEnabledSelector,
  tooManyContractsSelector,
} from 'store/selectors/contracts-selector';
import { employeesDropdownItemsSelector } from 'store/selectors/employee-selector';
import { activeMenuItemSelector } from 'store/selectors/menu-selectors';
import { profileSelector } from 'store/selectors/profile-selectors';
import {
  AVAILABILITIES__REQUEST_ALL_AVAILABILITIES,
  AVAILABILITIES__REQUEST_BASED_ON_FILTERS,
  COMPANY__COMPANY,
  CONTRACTS__ALREADY_CONFIRMED,
  CONTRACTS__ARE_YOU_SURE,
  CONTRACTS__CANCEL_COPY_MODE,
  CONTRACTS__COPY_MULTIPLE_CONTRACTS,
  CONTRACTS__COPY_SELECTED,
  CONTRACTS__COST_CENTER,
  CONTRACTS__DEFAULT_ERROR,
  CONTRACTS__DELETE_CONTRACT_WITH_ID_ERROR,
  CONTRACTS__DELETE_PAST_CONTRACT_ERROR,
  CONTRACTS__DELETE_SELECTED,
  CONTRACTS__DELETE_WEEKLY_CONTRACT_WARNING,
  CONTRACTS__FUNCTION,
  CONTRACTS__IS_SIGNED_WARNING,
  CONTRACTS__MULTIPLE_ERROR_MESSAGES,
  CONTRACTS__NEW_CONTRACT,
  CONTRACTS__NEW_CONTRACT_SLOT,
  CONTRACTS__NEW_CONTRACT_SLOT_DISABLED,
  CONTRACTS__PASTE_WEEKLY_CONTRACT,
  CONTRACTS__PLANNING_DELETED_BANNER,
  CONTRACTS__PLEASE_USE_FILTERS,
  CONTRACTS__PROCEED_QUESTION,
  CONTRACTS__SIGNED_CONTRACT_ERROR,
  CONTRACTS__TO_BE_CONFIRMED,
  EMPLOYEE__COMPANY_LOCATION,
  GENERAL__AGE,
  GENERAL__CANCEL_BUTTON,
  GENERAL__DAY,
  GENERAL__DOWNLOAD,
  GENERAL__NAME,
  GENERAL__NO,
  GENERAL__SELECT,
  GENERAL__WORKERCLASS,
  GENERAL__YES,
  HEADER__WEEK,
  MENU__CONTRACTS,
} from 'translations/constants';
import { IPostContractResponse } from 'types/contract';
import { IContractCard } from 'types/contract/IContractCard';
import ContractFilters from 'types/contract/IContractFilters';
import { addOrSubtractDays, generateCurrentWeekDays, getWeekDaysAbbreviations, treatAsUTC } from 'utils/helpers';
import { translateValidationMessage } from 'utils/translateValidationMessage';
import { ReactComponent as AvailabilityIconGreen } from '../../assets/icons/AvailabilitiesIconGreen.svg';
import { ReactComponent as AvailabilityIconGrey } from '../../assets/icons/AvailabilitiesIconGrey.svg';
import { ReactComponent as CalendarViewIconGreen } from '../../assets/icons/CalendarViewContractsGreen.svg';
import { ReactComponent as CalendarViewIconGrey } from '../../assets/icons/CalendarViewContractsGrey.svg';
import { ReactComponent as CloseIcon } from '../../assets/icons/CloseContractAction.svg';
import { ReactComponent as CopyIcon } from '../../assets/icons/CopyFull.svg';
import { ReactComponent as DeleteIcon } from '../../assets/icons/DeleteFull.svg';
import { ReactComponent as DownloadIcon } from '../../assets/icons/DownloadGreen.svg';
import { ReactComponent as ListViewIconGreen } from '../../assets/icons/ListViewContractsGreen.svg';
import { ReactComponent as ListViewIconGrey } from '../../assets/icons/ListViewContractsGrey.svg';
import ContractDetailPanel from '../../components/contract-detail-panel/ContractDetailPanel';
import FiltersMenu from '../../components/filters-menu';
import ContractsGridView from './grid/ContractsGridView';
import {
  generateAgeFilterOptions,
  generateCurrentWeekDaysList,
  getCurrentTime,
  getDaysToAddToContract,
  getISO8601WeekNumber,
  getISO8601Year,
  getTheNextTwoQuartersWeeks,
} from './helpers';
import ContractsListView from './list/ContractsListView';
import AddContractModal from './modals/add/AddContractModal';
import EditBeforePasteWrapper from './modals/add/EditBeforePasteWrapper';
import AddContractSlotModal from './modals/AddContractSlotModal';
import ConfirmContractModal from './modals/ConfirmContractModal';
import EditContractModal from './modals/edit/EditContractModal';
import {
  AddNewContractButton,
  CompanyUserFilter,
  IconWithMargin,
  StyledAddVendorSlotIcon,
  StyledContractIcon,
} from './styles';
import './styles.scss';
import { ErrorMessages, IContractToEdit, IDayProps, PageFilter } from './types';

const Contracts = (): JSX.Element => {
  const [t] = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const { pCompanyId, pYear, pWeek, pContractId, pContractDateId } = useParams();
  const vendors = useAppSelector(vendorsSelector);
  const activeItem = useAppSelector(activeMenuItemSelector);
  const contractsErrors = useAppSelector(getContractsErrors);
  const contractValidationErrors = useAppSelector(validateContractErrorMessageSelector);
  const contractValidationExtraInfo = useAppSelector(validateContractExtraMessageSelector);
  const contractsLoading = useAppSelector(contractsLoadingSelector);
  const isCopyModeEnabled = useAppSelector(isCopyModeEnabledSelector);
  const contractCardToCopy = useAppSelector(contractCardToCopySelector);
  const deleteContractsSuccess = useAppSelector(deleteContractsSuccessActionSelector);
  const saveContractSuccess = useAppSelector(saveContractSuccessSelector);
  const employeeDropdownItems = useAppSelector(employeesDropdownItemsSelector);
  const profile = useAppSelector(profileSelector);
  const isAdminOrAccountManager = profile.role === CompanyRole.ADMIN || profile.role === CompanyRole.ACCOUNT_MANAGER;
  const userCompanyId = profile.companyId;
  const isEmployee = profile.role === CompanyRole.EMPLOYEE;
  const allowedVendorRoles = useMemo(
    () => [CompanyRole.ADMIN, CompanyRole.COMPANY_MANAGER, CompanyRole.ACCOUNT_MANAGER],
    [],
  );
  const companiesDropdownItems = useAppSelector(companiesDropdownItemsSelector);
  const costCentersList = useAppSelector(costCentersDropdownItemsSelector);
  const [costCenters, setCostCenters] = useState<UPDropdownItem[]>(costCentersList);
  const functionsList = useAppSelector(functionsDropdownItemsSelector);
  const locationsList = useAppSelector(locationsDropdownItemsSelector);
  const companyAvailabilityFilters = useAppSelector(companyAvailabilityFiltersSelector);
  const defaultBreakTime = useAppSelector(defaultCompanyBreakSelector);

  const tooManyContacts = useAppSelector(tooManyContractsSelector);
  const [openTooManyContractsModal, setOpenTooManyContractsModal] = useState<boolean>(false);
  const [contractsErrorWithOpen, setContractsErrorWithOpen] = useState<any>([]);
  const [openAddModal, setOpenAddModal] = useState(false);
  const [openEditBeforePastingModal, setOpenEditBeforePastingModal] = useState<boolean>(false);
  const [contractIsSignedError, setContractIsSignedError] = useState<any>([]);
  const [contractIsSignedMessage, setContractIsSignedMessage] = useState<string>('');
  const [companyId, setCompanyId] = useState<string>(userCompanyId?.toString() ?? pCompanyId);
  const [costCenter, setCostCenter] = useState<string>('');
  const [selectedEmployee, setSelectedEmployee] = useState<string>('');
  const [selectedDay, setSelectedDay] = useState<string>('');
  const [selectedFunction, setSelectedFunction] = useState<string>('');
  const [contractType, setContractType] = useState<string>('');
  const [location, setLocation] = useState<string>('');
  const [openConfirmModal, setOpenConfirmModal] = useState<boolean>();
  const [openAlreadyConfirmed, setOpenAlreadyConfirmed] = useState<boolean>();
  const [cardToConfirmId, setCardToConfirmId] = useState<number | null>(null);
  const [detailPanelContractDateId, setDetailPanelContractDateId] = useState<number | null>(null);
  const [toBeConfirmed, setToBeConfirmed] = useState<string>('');
  const [showBottomGreenBar, setShowBottomGreenBar] = useState<boolean>(true);
  const [showDeletedPlanningBanner, setShowDeletedPlanningBanner] = useState<boolean>(false);
  const [openConfirmCloseModal, setOpenConfirmCloseModal] = useState<boolean>(false);
  const [isAdd, setIsAdd] = useState<boolean>(false);
  const [week, setWeek] = useState<Date | null>(getCurrentTime().date);
  const [weekForPasting, setWeekForPasting] = useState<Date>(getCurrentTime().date);
  const currentWeek = pWeek ? toNumber(pWeek) : getISO8601WeekNumber(week);
  const [weekNumberForPasting, setWeekNumberForPasting] = useState<number>(currentWeek);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [isViewOnlyMode, setIsViewOnlyMode] = useState(false);
  const [openContractSlotModal, setOpenContractSlotModal] = useState<boolean>(false);
  const [cardToEdit, setCardToEdit] = useState<IContractToEdit>(null);
  const [weeklyContractToCopy, setWeeklyContractToCopy] = useState<IContractToEdit>(null);
  const [openDeletePastContractModal, setOpenDeletePastContractModal] = useState<boolean>(false);
  const [openConfirmDeleteWeeklyModal, setOpenConfirmDeleteWeeklyModal] = useState<boolean>(false);
  const [openConfirmDeleteSignedModal, setOpenConfirmDeleteSignedModal] = useState<boolean>(false);
  const [selectedSignedContractsIds, setSelectedSignedContractsIds] = useState<number[]>([]);
  const [viewType, setViewType] = useState<ContractsPageViewType>(ContractsPageViewType.CALENDAR);
  const [employees, setEmployees] = useState<UPDropdownItem[]>([]);
  const [weeklyIdToCopy, setWeeklyIdToCopy] = useState<number>(null);
  const [openCopyMultipleModal, setOpenCopyMultipleModal] = useState<boolean>(false);
  const [year, setYear] = useState<number | null>(() =>
    pYear?.length > 1 ? toNumber(pYear) : getISO8601Year(new Date()),
  );
  const [checkedDays, setCheckedDays] = useState<{ date: Date; checked: boolean }[]>(
    generateCurrentWeekDays(currentWeek, year),
  );
  const [openValidationError, setOpenValidationError] = useState<boolean>(false);
  const [isValidationSnackbarHidden, setIsValidationSnackbarHidden] = useState<boolean>(false);
  const [filtersPopoverAnchorEl, setFiltersPopoverAnchorEl] = useState<null | HTMLElement>(null);
  const [age, setAge] = useState<string>('');
  const [disabledCreateButtons, setDisabledCreateButtons] = useState<boolean>(false);
  const [displayedContracts, setDisplayedContracts] = useState<number>(0);
  const [batchSize, setBatchSize] = useState<number>(ContractPageSize.Default);
  const [listViewType, setListViewType] = useState<number>(null);
  const [listViewCurrentTab, setListViewCurrentTab] = useState<number>(0);
  const [loaded, setLoaded] = useState<boolean>(false);
  const [saveContractsErrors, setSaveContractsErrors] = useState<IPostContractResponse[]>([]);
  const [contractCards, setContractCards] = useState<IContractCard[]>([]);
  const [toDeleteIds, setToDeleteIds] = useState<number[]>([]);

  // ─── Analytics: calendar filter tracking refs ────────────────────────────────
  const calendarInteractionIndexRef = useRef<number>(0);
  const pendingCalendarFiltersRef = useRef<{ changedFilters: string[]; filterState: Record<string, unknown> } | null>(
    null,
  );
  const prevCalendarFilterSnapshotRef = useRef<Record<string, unknown> | null>(null);
  // Incremented only when getContractCardsAction resolves successfully, so phase 2
  // fires strictly after the API responds – not on checkbox-toggle or copy-mode writes.
  const [contractCardsApiVersion, setContractCardsApiVersion] = useState<number>(0);
  // ─────────────────────────────────────────────────────────────────────────

  const toBeConfirmedOptions = useMemo(() => {
    return [
      { value: '0', label: t(GENERAL__NO) },
      { value: '1', label: t(GENERAL__YES) },
    ];
  }, [t]);

  const selectedContractCardIds = useMemo(() => {
    return contractCards.filter(card => card.checked).map(card => card.contractDateId);
  }, [contractCards]);

  const isDeletionPossible = useMemo(() => {
    if (isAdminOrAccountManager) return true;

    const pastContractFound = selectedContractCardIds.some(
      id => !contractCards.find(card => card.contractDateId === id)?.isEditable,
    );

    return !pastContractFound;
  }, [isAdminOrAccountManager, selectedContractCardIds, contractCards]);

  const weekDaysNames = getWeekDaysAbbreviations();

  const ageOptions = generateAgeFilterOptions();

  const contractsErrorMessages = useMemo(() => {
    return {
      default: t(CONTRACTS__DEFAULT_ERROR),
      signedPastContract: t(CONTRACTS__SIGNED_CONTRACT_ERROR),
      deletePastContract: t(CONTRACTS__DELETE_CONTRACT_WITH_ID_ERROR),
    };
  }, [t]);

  const bottomGreenBarActions = useMemo(() => {
    if (isCopyModeEnabled && Boolean(contractCardToCopy)) {
      return [
        {
          content: t(CONTRACTS__CANCEL_COPY_MODE),
          icon: <CloseIcon />,
        },
      ];
    }
    return [
      {
        content: t(CONTRACTS__DELETE_SELECTED),
        icon: <DeleteIcon />,
      },
      {
        content: t(CONTRACTS__COPY_SELECTED),
        icon: <CopyIcon />,
        gtmEvent: 'contracts-copy-selection',
      },
    ];
  }, [t, contractCardToCopy, isCopyModeEnabled]);

  const checkConfirmedFilter = useCallback(() => {
    if (toBeConfirmed === '1') {
      return false;
    }
    if (toBeConfirmed === '0') {
      // if to be confirmed is 0, that is toBeConfirmed = "no" so we want to show confirmed contracts
      return true;
    }
    return null;
  }, [toBeConfirmed]);

  const createFilters = useCallback(
    (pageSize = 0) => {
      if (pageSize > 0) {
        setDisplayedContracts(displayedContracts + pageSize + batchSize);
        setBatchSize(batchSize + pageSize);
      }

      const filter = {
        year,
        week: currentWeek,
        pageNumber: 1,
        pageSize: batchSize + pageSize,
        costCenterId: toNumber(costCenter),
        functionId: toNumber(selectedFunction),
        workerClass: contractType,
        locationId: toNumber(location),
        confirmHours: checkConfirmedFilter(),
        employeeIds: selectedEmployee && selectedEmployee !== '' ? [+selectedEmployee] : [],
        date: selectedDay ? new Date(selectedDay).toISOString() : null,
      };

      return filter;
    },
    [
      year,
      currentWeek,
      costCenter,
      selectedFunction,
      contractType,
      location,
      selectedEmployee,
      selectedDay,
      displayedContracts,
      batchSize,
      checkConfirmedFilter,
    ],
  );

  const fetchContractCards = useCallback(
    async (newFilter?: ContractFilters) => {
      if (!companyId || toNumber(companyId) === 0) return;

      const f = newFilter ?? createFilters();

      try {
        const result = await dispatch(
          getContractCardsAction({
            companyId: +companyId,
            filter: f,
          }),
        );

        if (getContractCardsAction.fulfilled.match(result)) {
          setContractCards(result.payload);
          setContractCardsApiVersion(v => v + 1);
        }
      } catch (error) {
        console.error('Error fetching contract cards:', error);
      }
    },
    [companyId, dispatch, createFilters],
  );

  const createDefaultListViewFilters = useCallback((): ContractFilters => {
    const filter = {
      year,
      week: currentWeek,
      pageNumber: 1,
      pageSize: ContractPageSize.DefaultListView,
      costCenterId: toNumber(costCenter),
      functionId: toNumber(selectedFunction),
      workerClass: contractType,
      locationId: toNumber(location),
      confirmHours: checkConfirmedFilter(),
      employeeIds: selectedEmployee && selectedEmployee !== '' ? [+selectedEmployee] : [],
      date: selectedDay ? new Date(selectedDay).toISOString() : null,
    };

    return filter;
  }, [
    year,
    currentWeek,
    costCenter,
    selectedFunction,
    contractType,
    location,
    selectedEmployee,
    selectedDay,
    checkConfirmedFilter,
  ]);

  const workerclassList = useMemo(() => CoefficientsPerWorkerClassOptions(), []);

  const listViewFilters = useMemo((): PageFilter[] => {
    const filters: PageFilter[] = [
      {
        label: t(EMPLOYEE__COMPANY_LOCATION),
        value: location,
        setValue: setLocation,
        items: locationsList,
        component: UPDropdownWithSearch,
        gtmEvent: 'list-view-branch-filter',
      },
      {
        label: t(GENERAL__WORKERCLASS),
        value: contractType,
        setValue: setContractType,
        items: workerclassList,
        component: UPDropdown,
        canBeEmpty: true,
        gtmEvent: 'list-view-workerclass-filter',
      },
      {
        label: t(CONTRACTS__FUNCTION),
        value: selectedFunction,
        setValue: setSelectedFunction,
        items: functionsList,
        component: UPDropdownWithSearch,
        gtmEvent: 'list-view-function-filter',
      },
    ];

    return filters;
  }, [contractType, functionsList, location, locationsList, selectedFunction, t, workerclassList]);

  const calendarViewFilters = useMemo((): PageFilter[] => {
    const filters: PageFilter[] = [
      {
        label: t(EMPLOYEE__COMPANY_LOCATION),
        value: location,
        setValue: setLocation,
        items: locationsList,
        component: UPDropdownWithSearch,
        gtmEvent: 'calendar-view-branch-filter',
      },
      {
        label: t(GENERAL__WORKERCLASS),
        value: contractType,
        setValue: setContractType,
        items: workerclassList,
        component: UPDropdown,
        canBeEmpty: true,
        gtmEvent: 'calendar-view-workerclass-filter',
      },
      {
        label: t(CONTRACTS__FUNCTION),
        value: selectedFunction,
        setValue: setSelectedFunction,
        items: functionsList,
        component: UPDropdownWithSearch,
        gtmEvent: 'calendar-view-function-filter',
      },
      {
        label: t(CONTRACTS__TO_BE_CONFIRMED),
        value: toBeConfirmed,
        setValue: setToBeConfirmed,
        items: toBeConfirmedOptions,
        component: UPDropdown,
        canBeEmpty: true,
        gtmEvent: 'calendar-view-to-be-confirmed-filter',
      },
    ];

    return filters;
  }, [
    contractType,
    functionsList,
    location,
    locationsList,
    selectedFunction,
    t,
    toBeConfirmed,
    toBeConfirmedOptions,
    workerclassList,
  ]);

  const availabilityViewFilters = useMemo((): PageFilter[] => {
    const filters: PageFilter[] = [
      {
        label: t(EMPLOYEE__COMPANY_LOCATION),
        value: location,
        setValue: setLocation,
        items: locationsList,
        component: UPDropdownWithSearch,
      },
      {
        label: t(GENERAL__WORKERCLASS),
        value: contractType,
        setValue: setContractType,
        items: workerclassList,
        component: UPDropdown,
        canBeEmpty: true,
      },
      {
        label: t(GENERAL__AGE),
        value: age,
        setValue: setAge,
        items: ageOptions,
        component: UPDropdown,
        canBeEmpty: true,
      },
    ];

    return filters;
  }, [age, ageOptions, contractType, location, locationsList, t, workerclassList]);

  const isThereAnyContractCardChecked = useMemo(() => {
    return contractCards.some((c: IContractCard) => c.checked === true);
  }, [contractCards]);

  const onChangeWeek = useCallback(
    (timePeriod: string, date: Date) => {
      const timeMultiplier = timePeriod === 'past' ? -1 : 1;
      const newCheckedDays: { date: Date; checked: boolean }[] = [];
      for (let counter = 1; counter <= 7; ++counter) {
        const day = {
          date: addOrSubtractDays(counter * timeMultiplier, date),
          checked: false,
        };
        if (timePeriod === 'past') {
          newCheckedDays.unshift(day);
        } else {
          newCheckedDays.push(day);
        }
      }

      setWeek(treatAsUTC(newCheckedDays[0].date));
      setYear(getISO8601Year(newCheckedDays[0].date));
      setCheckedDays(newCheckedDays);
    },
    [setWeek, setYear, setCheckedDays],
  );

  const onClearAll = () => {
    setShowBottomGreenBar(false);
    const contractsCleared = contractCards.map(card => {
      return {
        ...card,
        checked: false,
      };
    });
    setContractCards(contractsCleared);
  };

  const makeDeleteRequest = (ids: number[], confirmedDeleteSigned?: boolean) => {
    dispatch(
      deleteContractsAction({
        companyId: toNumber(companyId),
        contractsIds: ids,
        year,
        weekNumber: currentWeek,
        confirmedDeleteSigned,
      }),
    ).then(() => {
      fetchContractCards();
    });
  };

  const onDeleteCheckedContracts = (ids: number[]) => {
    const newContracts = contractCards.filter(card => ids.includes(card.contractDateId));
    const pastContractFound = newContracts.find(card => !card.isEditable);
    if (pastContractFound && !isAdminOrAccountManager) setOpenDeletePastContractModal(true);
    else if (newContracts.some(card => card.isWeekly)) {
      setOpenConfirmDeleteWeeklyModal(true);
      setToDeleteIds(ids);
    } else {
      makeDeleteRequest(ids);
    }
  };

  const onClickReopenConfirmation = (contractDateId: number) => {
    dispatch(
      reopenConfirmationAction({
        companyId: toNumber(companyId),
        contractDateId,
        year,
        weekNumber: currentWeek,
      }),
    ).then(() => {
      fetchContractCards();
    });
  };

  const onCloseCopyMultipleModal = () => {
    dispatch(toggleCopyModeAction());
    const uncheckedContracts = contractCards.map(card => {
      return {
        ...card,
        checked: false,
      };
    });
    setWeeklyIdToCopy(null);
    setOpenCopyMultipleModal(false);
    dispatch(directlyCardToCopyAction({ id: null }));
    setContractCards(uncheckedContracts);
  };

  const onClickEditCard = (id: number, isViewOnly = false) => {
    navigate(`/contracts/edit/${companyId}/${id}`);
    setIsViewOnlyMode(isViewOnly);
  };

  useEffect(() => {
    if (pContractId) {
      const showDeletedPlanningFallback = () => {
        // Deep link opened for a planning that was deleted after the notification was sent:
        // fall back to the planning overview and show the deleted-planning banner.
        if ((routerLocation.state as { fromPlanningDeepLink?: boolean })?.fromPlanningDeepLink) {
          setShowDeletedPlanningBanner(true);
          navigate('/contracts', { replace: true });
        }
      };

      dispatch(
        getContractByIdAction({
          companyId: +companyId,
          contractId: +pContractId,
        }),
      )
        .unwrap()
        .then(contract => {
          // Deleting a planning only soft-deletes its date rows; the Contract row survives, so
          // the API returns 200 with the contract's identity fields populated but an empty dates
          // array (never a 404). A thrown error only happens for a missing/cross-company id, so
          // the .catch below won't fire for a deleted planning — detect the empty dates here.
          if (!contract || !contract.dates || contract.dates.length === 0) {
            showDeletedPlanningFallback();
            return;
          }
          setCardToEdit(contract);
          // Opening the modal directly from the route (deep link, refresh, bookmark) bypasses
          // the card, which is where view-vs-edit is normally decided by role. An employee only
          // ever has view rights on a planning, so force view-only here to avoid granting edit.
          if (isEmployee) {
            setIsViewOnlyMode(true);
          }
          setOpenEditModal(true);
        })
        .catch(showDeletedPlanningFallback);
    }
  }, [pContractId, dispatch, companyId, navigate, routerLocation.state, isEmployee]);

  const isAdmin = profile.role === CompanyRole.ADMIN;

  const useDetailPanel = isAdmin || profile?.featureTimeRegistrationEnabled;

  useEffect(() => {
    if (pContractDateId && cardToConfirmId === null && detailPanelContractDateId === null) {
      if (useDetailPanel) {
        setDetailPanelContractDateId(toNumber(pContractDateId));
      } else {
        setCardToConfirmId(toNumber(pContractDateId));
        setOpenConfirmModal(true);
      }
    }
  }, [pContractDateId, cardToConfirmId, detailPanelContractDateId, useDetailPanel]);

  const onClickConfirmCard = (cardId: number) => {
    const cardToConfirm = contractCards.find(card => card.contractDateId === cardId);
    if (cardToConfirm.isConfirmed) {
      setOpenAlreadyConfirmed(true);
    } else if (useDetailPanel) {
      setDetailPanelContractDateId(cardToConfirm.contractDateId);
    } else {
      setCardToConfirmId(cardToConfirm.contractDateId);
      setOpenConfirmModal(true);
    }
  };

  const handleDetailPanelClose = useCallback(() => {
    setDetailPanelContractDateId(null);
    dispatch(setBulkConfirmErrorsAction({}));
  }, [dispatch]);

  const isPlanningModalOpen = openAddModal || openEditModal || openContractSlotModal;

  useEffect(() => {
    if (!contractValidationErrors || contractsLoading) setIsValidationSnackbarHidden(false);
  }, [contractValidationErrors, contractsLoading]);

  useEffect(() => {
    if (isValidationSnackbarHidden && !isPlanningModalOpen) {
      dispatch(validateContractErrorMessageAction(''));
    }
  }, [dispatch, isPlanningModalOpen, isValidationSnackbarHidden]);

  const onAcceptClosingEditModal = () => {
    navigate('/contracts');
    setOpenEditModal(false);
    setOpenConfirmCloseModal(false);
    dispatch(validateContractSuccessAction(false));
    dispatch(validateContractErrorMessageAction(''));
    dispatch(validateContractErrorMessageExtraInfoAction(''));
    dispatch(validateContractErrorMessageRowAction(null));
    dispatch(getCostCentersDropdownItemsAction({ companyId: toNumber(companyId) }));
  };

  const onCloseDeleteSignedModal = () => {
    setOpenConfirmDeleteSignedModal(false);
    dispatch(setErrors([]));
    setContractIsSignedError([]);
  };

  const onWeekPickerChange = (value: Date) => {
    setWeek(value);
    setSelectedDay('');
    const newWeek = getISO8601WeekNumber(value);
    setCheckedDays(generateCurrentWeekDays(newWeek, getISO8601Year(value), value));
  };

  const handleErrorClose = (id: number) => {
    const auxErrors = contractsErrorWithOpen.map((c: any) => {
      if (c.id === id)
        return {
          ...c,
          open: false,
        };
      return c;
    });
    setContractsErrorWithOpen(auxErrors);
  };

  const onCopyCheckedContracts = () => {
    const checkedContracts = contractCards.filter(c => c.checked);
    const uniqueIds = new Set(checkedContracts.map(c => c.contractId));
    dispatch(toggleCopyModeAction());
    if (uniqueIds.size === 1) {
      const isSingleWeeklyContract = checkedContracts.every(c => c.contractId);
      if (isSingleWeeklyContract) {
        const { contractId } = checkedContracts[0];
        setWeeklyIdToCopy(contractId);
      }
    }
    setOpenCopyMultipleModal(true);
  };

  const onPaste = async (newDate: Date) => {
    const date = dayjs(new Date(newDate)).format('DD/MM/YYYY');
    await dispatch(
      pasteSingleContractAction({
        companyId: +companyId,
        contractDateId: contractCardToCopy.contractDateId,
        date,
      }),
    ).then(() => {
      fetchContractCards();
    });
  };

  const onPasteMultipleContracts = async (weeksToPasteTo: number[]) => {
    const selectedContractCards = contractCards.filter(card => selectedContractCardIds.includes(card.contractDateId));
    const contractIds = Array.from(new Set(selectedContractCards.map(card => card.contractId)));
    dispatch(pasteMultipleContractsAction({ contractIds, weeksToPasteTo, companyId: +companyId })).then(response => {
      const errorResponses = response.payload as IPostContractResponse[];
      setSaveContractsErrors(errorResponses.filter((r: IPostContractResponse) => !r.success));
      fetchContractCards();
    });
  };

  // Effect to fetch contract cards when needed
  useEffect(() => {
    if (viewType === ContractsPageViewType.CALENDAR && companyId && !isEmployee) {
      fetchContractCards();
    }
  }, [viewType, companyId, isEmployee, fetchContractCards]);

  // Reset filter baseline only on view or company switches, not on every filter state change.
  // This must stay separate from the fetchContractCards effect (which changes on every filter
  // state update) to avoid wiping the baseline before phase 1 can store a pending entry.
  useEffect(() => {
    prevCalendarFilterSnapshotRef.current = null;
  }, [viewType, companyId]);

  // Reset filter baseline only on view or company switches, not on every filter state change.
  // This must stay separate from the fetchContractCards effect (which changes on every filter
  // state update) to avoid wiping the baseline before phase 1 can store a pending entry.
  useEffect(() => {
    prevCalendarFilterSnapshotRef.current = null;
  }, [viewType, companyId]);

  const onEditBeforePastingWeekly = async (weekToPasteTo: number) => {
    dispatch(
      getContractByIdAction({
        companyId: toNumber(companyId),
        contractId: weeklyIdToCopy,
      }),
    )
      .unwrap()
      .then(contract => {
        setWeeklyContractToCopy(contract);
      });
    const daysToAdd = getDaysToAddToContract(currentWeek, weekToPasteTo);
    setWeekForPasting(addOrSubtractDays(daysToAdd, week));
    setWeekNumberForPasting(weekToPasteTo);
    onCloseCopyMultipleModal();
    setOpenEditBeforePastingModal(true);
  };

  const onClickFilterButton = event => {
    setFiltersPopoverAnchorEl(event.currentTarget);
  };

  const onCloseFilterPopover = () => {
    setFiltersPopoverAnchorEl(null);
  };

  const onChangeLocationFilter = () => {
    dispatch(
      setCompanyAvailabilityFiltersAction([
        { filterType: CompanyAvailabilityFilterType.LOCATION_ID, value: toNumber(location) },
      ]),
    );
  };

  const onChangeWorkerclassFilter = () => {
    dispatch(
      setCompanyAvailabilityFiltersAction([
        { filterType: CompanyAvailabilityFilterType.WORKERCLASS, value: contractType },
      ]),
    );
  };

  const onChangeAgeFilter = () => {
    dispatch(setCompanyAvailabilityFiltersAction([{ filterType: CompanyAvailabilityFilterType.AGE, value: age }]));
  };

  useEffect(onChangeLocationFilter, [location, dispatch]);

  useEffect(onChangeWorkerclassFilter, [contractType, dispatch]);

  useEffect(onChangeAgeFilter, [age, dispatch]);

  const onClearCompanyAvailabilityFilters = () => {
    setLocation('');
    setContractType('');
    setAge('');
    setFiltersPopoverAnchorEl(null);
    dispatch(clearCompanyAvailabilityFiltersAction());
  };

  const onClearContractsFilters = () => {
    setLocation('');
    setContractType('');
    setSelectedFunction('');
    setToBeConfirmed('');
  };

  const updateCompanyAvailabilityMainFilters = () => {
    const filters = [
      {
        filterType: CompanyAvailabilityFilterType.WEEK,
        value: currentWeek,
      },
      {
        filterType: CompanyAvailabilityFilterType.YEAR,
        value: year,
      },
      {
        filterType: CompanyAvailabilityFilterType.COMPANY_ID,
        value: companyId ? toNumber(companyId) : 0,
      },
    ];

    dispatch(setCompanyAvailabilityFiltersAction(filters));
  };

  useEffect(updateCompanyAvailabilityMainFilters, [currentWeek, year, dispatch, companyId]);

  useEffect(() => {
    if (openValidationError) {
      setOpenValidationError(false);
    }
  }, [openValidationError]);

  useEffect(() => {
    if (activeItem !== t(MENU__CONTRACTS)) {
      dispatch(setMenuStatusAction(t(MENU__CONTRACTS)));
    }
  }, [activeItem, dispatch, t]);

  useEffect(() => {
    const numericCompanyId = toNumber(companyId);
    if (numericCompanyId > 0) {
      getAnalyticsService().setCompanyId(numericCompanyId);
    }
  }, [companyId]);

  // ─── Analytics: filters — phase 1 (detect calendar filter change) ───────────────────
  useEffect(() => {
    if (viewType !== ContractsPageViewType.CALENDAR) return;
    const snapshot: Record<string, unknown> = {
      locationId: location,
      workerClass: contractType,
      functionId: selectedFunction,
      confirmHours: toBeConfirmed,
      employeeIds: selectedEmployee,
      costCenterId: costCenter,
      date: selectedDay,
    };
    const prev = prevCalendarFilterSnapshotRef.current;
    if (prev === null) {
      prevCalendarFilterSnapshotRef.current = snapshot;
      return;
    }
    const changedFilters = Object.keys(snapshot).filter(k => prev[k] !== snapshot[k]);
    if (changedFilters.length === 0) {
      prevCalendarFilterSnapshotRef.current = snapshot;
      return;
    }
    pendingCalendarFiltersRef.current = { changedFilters, filterState: snapshot };
    prevCalendarFilterSnapshotRef.current = snapshot;
  }, [
    viewType,
    companyId,
    location,
    contractType,
    selectedFunction,
    toBeConfirmed,
    selectedEmployee,
    costCenter,
    selectedDay,
  ]);

  // ─── Analytics: filters — phase 2 (fire after API responds) ─────────────────────
  // Depends on contractCardsApiVersion, which increments only inside fetchContractCards
  // on a successful API response. This prevents checkbox-toggle / copy-mode writes to
  // contractCards from consuming the pending entry before the real response arrives.
  useEffect(() => {
    if (!pendingCalendarFiltersRef.current) return;
    const { changedFilters, filterState } = pendingCalendarFiltersRef.current;
    pendingCalendarFiltersRef.current = null;
    trackFiltersChanged({
      changedFilters,
      filterState,
      rowsVisibleCount: contractCards.length,
      interactionIndex: calendarInteractionIndexRef.current,
    });
    calendarInteractionIndexRef.current += 1;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractCardsApiVersion]);
  // ─────────────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!companyId) {
      setEmployees([]);
      dispatch(setErrors([]));
      setContractIsSignedError([]);
    }
  }, [companyId, viewType, dispatch]);

  useEffect(() => {
    if (contractsErrors) {
      const auxErrors = contractsErrors.map((c: any) => {
        return { ...c, open: true };
      });
      setContractsErrorWithOpen(auxErrors);
      if (auxErrors.some((c: any) => c.type === 'signedPastContract') && isAdminOrAccountManager) {
        setContractIsSignedError(auxErrors.filter((c: any) => c.type === 'signedPastContract'));
        setContractsErrorWithOpen(auxErrors.filter((c: any) => c.type !== 'signedPastContract'));
      }
    }

    if (tooManyContacts) {
      setOpenTooManyContractsModal(true);
    }
  }, [contractsErrors, tooManyContacts, isAdminOrAccountManager]);

  useEffect(() => {
    if (contractIsSignedError.length > 0 && companyId) {
      let message = `${t(CONTRACTS__IS_SIGNED_WARNING)}: `;

      const contractsIds: SetStateAction<number[]> = [];
      contractIsSignedError.forEach((contractError: any) => contractsIds.push(contractError.contractId));
      setSelectedSignedContractsIds(contractsIds);
      setOpenConfirmDeleteSignedModal(true);

      const coveredContracts: number[] = [];
      contractIsSignedError.forEach((c: any) => {
        if (coveredContracts.filter(coveredContract => coveredContract === c.contractId).length === 0) {
          const contractDetails = contractCards.filter(card => card.contractDateId === c.contractId);
          const relatedContracts = contractCards.filter(
            relatedCard => relatedCard.contractId === contractDetails.at(0).contractId,
          );
          if (relatedContracts.length > 0) {
            coveredContracts.push(...relatedContracts.map(r => r.contractDateId));
            message += `\n'${contractDetails.at(0).fullName}: `;
            const startDate = relatedContracts.map(rc => rc.date).sort()[0];
            const endDate = relatedContracts
              .map(rc => rc.date)
              .sort()
              .reverse()[0];
            message += `${dayjs(startDate).format('DD/MM/YYYY')} - ${dayjs(endDate).format('DD/MM/YYYY')}'`;
          }
        }
      });
      message += `\n${t(CONTRACTS__PROCEED_QUESTION)}`;
      setContractIsSignedMessage(message);
    } else {
      setOpenConfirmDeleteSignedModal(false);
      setSelectedSignedContractsIds([]);
    }
  }, [contractIsSignedError, contractCards, t, companyId]);

  // Keep the working company aligned with the active (JWT) company after a switch, so the
  // function/location pickers never offer another company's items that then fail validation.
  // Admin/Account Manager pick the company via the in-page filter (header-based), so skip them.
  useEffect(() => {
    if (!isAdminOrAccountManager && userCompanyId) {
      setCompanyId(userCompanyId.toString());
    }
  }, [userCompanyId, isAdminOrAccountManager]);

  // Company-only data: dropdowns and lookups that only depend on companyId
  useEffect(() => {
    if (companyId && !isEmployee) {
      dispatch(getCostCentersAction(toNumber(companyId)));
      dispatch(getCostCentersDropdownItemsAction({ companyId: toNumber(companyId) }));
      dispatch(getFunctionsAsDropdownItemsAction(toNumber(companyId)));
      dispatch(getLocationsDropdownItemsAction({ companyId: toNumber(companyId) }));
      dispatch(getDefaultCompanyBreakTimeAction(toNumber(companyId)));

      if (allowedVendorRoles.includes(profile.role)) {
        dispatch(getVendorsAction(companyId));
      }
    }

    if (isEmployee) {
      dispatch(getCostCentersAction(+companyId));
      dispatch(getCostCentersDropdownItemsAction({ companyId: +companyId }));
    }
  }, [companyId, dispatch, isEmployee, allowedVendorRoles, profile]);

  // Week/year-sensitive data: list view contracts and employee card fetching
  useEffect(() => {
    if (companyId && !isEmployee) {
      if (viewType === ContractsPageViewType.LIST) {
        const f = createDefaultListViewFilters();
        f.listViewType = listViewType ?? ContractListViewTypes.Future;
        dispatch(
          getContractsInListViewAction({
            companyId: toNumber(companyId),
            filter: f,
          }),
        );
      }

      setLoaded(false);
    }

    if (!companyId && !isEmployee && !loaded && viewType === ContractsPageViewType.LIST) {
      const f = createDefaultListViewFilters();
      f.listViewType = listViewType ?? ContractListViewTypes.Future;
      f.pageNumber = 1;
      f.pageSize = 0;
      dispatch(
        getContractsInListViewAction({
          companyId: toNumber(companyId),
          filter: f,
        }),
      );

      setLoaded(true);
    }

    if (isEmployee) {
      fetchContractCards();
    }
  }, [
    companyId,
    year,
    currentWeek,
    dispatch,
    isEmployee,
    viewType,
    listViewType,
    loaded,
    createDefaultListViewFilters,
    fetchContractCards,
  ]);

  useEffect(() => {
    if (isAdminOrAccountManager) {
      dispatch(getCompaniesAsDropdownItemsAction());
    }
  }, [isAdminOrAccountManager, dispatch]);

  useEffect(() => {
    if (isThereAnyContractCardChecked || (isCopyModeEnabled && contractCardToCopy)) {
      setShowBottomGreenBar(true);
    } else {
      setShowBottomGreenBar(false);
    }
  }, [isThereAnyContractCardChecked, isCopyModeEnabled, contractCardToCopy]);

  useEffect(() => {
    let shouldSetCheckedDays = false;
    const newCheckedDays = checkedDays.map((day: IDayProps, index: number) => {
      let allChecked = true;
      const sameDayContracts = contractCards.filter(card => {
        return (
          day.date.getDate() === new Date(card.date).getDate() && day.date.getMonth() === new Date(card.date).getMonth()
        );
      });
      if (sameDayContracts.length === 0) allChecked = false;

      sameDayContracts.forEach(card => {
        allChecked = allChecked && card.checked;
      });

      if (day.checked !== allChecked) shouldSetCheckedDays = true;
      if (day.numberOfItems !== sameDayContracts.length) shouldSetCheckedDays = true;
      return {
        ...day,
        dayOfWeek: weekDaysNames[index],
        checked: allChecked,
        numberOfItems: sameDayContracts.length,
      };
    });

    if (shouldSetCheckedDays) setCheckedDays(newCheckedDays);
  }, [checkedDays, contractCards, weekDaysNames]);

  useEffect(() => {
    if (deleteContractsSuccess || (isAdd && saveContractSuccess)) {
      fetchContractCards();
    }
  }, [deleteContractsSuccess, isAdd, saveContractSuccess, fetchContractCards]);

  useEffect(() => {
    if (!isEmployee && companyId) dispatch(getEmployeesDropdownItems(+companyId));
  }, [companyId, dispatch, isEmployee]);

  useEffect(() => {
    if (employeeDropdownItems && companyId) {
      setEmployees(employeeDropdownItems);
    }

    if (costCentersList) {
      setCostCenters(costCentersList);
    }

    if (!companyId) {
      setEmployees([]);
      setCostCenter('');
      setCostCenters([]);
    }
  }, [employeeDropdownItems, companyId, costCentersList]);

  const filterContracts = useCallback(
    (updates: { key: string; value: any }[] = []) => {
      if (toNumber(companyId) > 0) {
        const filter = createFilters();
        if (updates.length > 0) {
          updates.forEach(update => {
            filter[update.key] = update.value;
          });
        }

        fetchContractCards(filter);
      }
    },
    [companyId, createFilters, fetchContractCards],
  );

  const fetchMoreContracts = useCallback(() => {
    const filter = createFilters(ContractPageSize.Default);
    // maybe use this with contract cards
    // dispatch(
    //   getContractsAction({
    //     companyId: toNumber(companyId),
    //     filter,
    //   }),
    // );
  }, [createFilters]);

  const canViewAvailabilities = (): boolean => {
    const managerRoles = [CompanyRole.COMPANY_MANAGER, CompanyRole.HR_MANAGER, CompanyRole.LOCATION_MANAGER];

    return isAdminOrAccountManager || (profile?.featureAvailabilityEnabled && managerRoles.includes(profile.role));
  };

  const exportAvailabilities = () => {
    dispatch(
      exportCompanyAvailabilityAction({
        companyId: toNumber(companyId),
        locationId: companyAvailabilityFilters.locationId,
        workerclass: companyAvailabilityFilters.workerclass,
        name: companyAvailabilityFilters.name,
        age: companyAvailabilityFilters.age,
        week: companyAvailabilityFilters.week,
        year: companyAvailabilityFilters.year,
      }),
    );
  };

  const reloadContracts = () => {
    if (viewType === ContractsPageViewType.CALENDAR) {
      fetchContractCards();
    }

    if (viewType === ContractsPageViewType.LIST) {
      const f = createDefaultListViewFilters();
      f.listViewType = listViewType ?? ContractListViewTypes.Future;
      dispatch(
        getContractsInListViewAction({
          companyId: toNumber(companyId),
          filter: f,
        }),
      );
    }
  };

  const onDownloadErrors = () => {
    const errors = saveContractsErrors.map((error: IPostContractResponse) => {
      return {
        ...error,
        message: translateValidationMessage(t, error.message),
      };
    });
    dispatch(downloadContractErrorsAction({ errors }));
  };

  const getSnackBarContent = () => {
    return saveContractsErrors.length === 1 ? (
      `${translateValidationMessage(t, saveContractsErrors[0].message)} : ${saveContractsErrors[0].details}`
    ) : (
      <div>
        {t(CONTRACTS__MULTIPLE_ERROR_MESSAGES)} <UPLinkButton onClick={onDownloadErrors} text={t(GENERAL__DOWNLOAD)} />
      </div>
    );
  };

  const onWeekPickerStatusChange = useCallback((newValue: any) => {
    onWeekPickerChange(newValue);
    setYear(getISO8601Year(new Date(newValue)));
  }, []);

  return (
    <Box>
      <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', flexDirection: 'row' }}>
          {!isEmployee && (
            <Box display="flex">
              <AddNewContractButton
                text={
                  <Tooltip title={t(CONTRACTS__NEW_CONTRACT)} placement="top">
                    <Box display="flex" alignItems="center">
                      <StyledContractIcon />
                    </Box>
                  </Tooltip>
                }
                onClick={() => {
                  dispatch(validateContractSuccessAction(false));
                  setOpenAddModal(true);
                }}
                disabled={
                  (isAdminOrAccountManager && !companyId) ||
                  isCopyModeEnabled ||
                  (viewType === ContractsPageViewType.AVAILABILITY && disabledCreateButtons)
                }
                gtmEvent="contracts_add_new_contract"
                id="contract-dashboard-add-new-contract-button"
              />
              <AddNewContractButton
                text={
                  <Tooltip
                    title={vendors.length ? t(CONTRACTS__NEW_CONTRACT_SLOT) : t(CONTRACTS__NEW_CONTRACT_SLOT_DISABLED)}
                    placement="top"
                  >
                    <Box display="flex" alignItems="center">
                      <Typography fontSize="xx-large"> + </Typography>
                      <StyledAddVendorSlotIcon />
                    </Box>
                  </Tooltip>
                }
                onClick={() => {
                  dispatch(validateContractSuccessAction(false));
                  setOpenContractSlotModal(true);
                }}
                disabled={
                  (isAdminOrAccountManager && !companyId) ||
                  isCopyModeEnabled ||
                  vendors.length === 0 ||
                  (viewType === ContractsPageViewType.AVAILABILITY && disabledCreateButtons)
                }
                gtmEvent="contracts-add-contract-slot"
              />
            </Box>
          )}
          <UPWeekPicker
            value={week}
            weekValue={currentWeek}
            onChange={newValue => {
              onWeekPickerStatusChange(newValue);
            }}
            label={t(HEADER__WEEK)}
            className="datepicker--white-background"
          />
          {isAdminOrAccountManager && (
            <Box sx={{ width: '280px', display: 'inline-block', marginLeft: '8px' }}>
              <UPDropdownWithSearch
                value={companyId}
                onChange={selectedItem => {
                  setCompanyId(selectedItem);
                  setSelectedEmployee('');
                  setCostCenter('');
                }}
                placeholder={t(GENERAL__SELECT)}
                items={companiesDropdownItems}
                label={t(COMPANY__COMPANY)}
                disabled={isCopyModeEnabled}
                disableClearable={false}
              />
            </Box>
          )}
          {viewType === ContractsPageViewType.LIST && (
            <Box sx={{ width: '178px', display: 'inline-block', marginLeft: '8px' }}>
              <UPDropdownWithSearch
                value={selectedDay}
                onChange={selectedItem => {
                  setSelectedDay(selectedItem || null);
                  filterContracts([{ key: 'date', value: selectedItem ? new Date(selectedItem) : null }]);
                }}
                placeholder={t(GENERAL__SELECT)}
                items={generateCurrentWeekDaysList(currentWeek, year, weekDaysNames)}
                label={t(GENERAL__DAY)}
                disableClearable={false}
                gtmEvent="contracts-list-view-day-filter"
              />
            </Box>
          )}
          {!isEmployee && viewType !== ContractsPageViewType.AVAILABILITY && (
            <Box sx={{ width: '244px', display: 'inline-block', marginLeft: '8px' }}>
              <UPDropdownWithSearch
                value={selectedEmployee}
                onChange={selectedItem => {
                  setSelectedEmployee(selectedItem || null);
                  filterContracts([{ key: 'employeeIds', value: selectedItem ? [toNumber(selectedItem)] : [] }]);
                }}
                placeholder={t(GENERAL__SELECT)}
                items={employees}
                label={t(GENERAL__NAME)}
                disableClearable={false}
                gtmEvent="contracts-employee-filter"
              />
            </Box>
          )}
        </Box>
        {!isEmployee && viewType !== ContractsPageViewType.AVAILABILITY && (
          <Box sx={{ display: 'flex', flexDirection: 'row' }}>
            <CompanyUserFilter>
              <UPDropdownWithSearch
                value={costCenter}
                onChange={selectedItem => {
                  setCostCenter(selectedItem || null);
                  filterContracts([{ key: 'costCenterId', value: selectedItem ? toNumber(selectedItem) : null }]);
                }}
                placeholder={t(GENERAL__SELECT)}
                items={costCenters}
                label={t(CONTRACTS__COST_CENTER)}
                disabled={isCopyModeEnabled}
                disableClearable={false}
                gtmEvent="contracts-cost-centre-filter"
              />
            </CompanyUserFilter>
          </Box>
        )}
        {!isEmployee && viewType === ContractsPageViewType.AVAILABILITY && (
          <Box sx={{ display: 'flex', flexDirection: 'row', height: '48px' }}>
            <CompanyUserFilter>
              <AddNewContractButton
                text={
                  <Tooltip title={t(AVAILABILITIES__REQUEST_BASED_ON_FILTERS)} placement="top">
                    {t(AVAILABILITIES__REQUEST_ALL_AVAILABILITIES)}
                  </Tooltip>
                }
                onClick={() => {
                  dispatch(
                    requestAvailabilityFilteredAction({
                      companyId: toNumber(companyId),
                      month: checkedDays[6].date.getMonth() + 1,
                      locationId: companyAvailabilityFilters.locationId,
                      workerclass: companyAvailabilityFilters.workerclass,
                      age: companyAvailabilityFilters.age,
                    }),
                  );
                }}
                disabled={!companyId}
              />
            </CompanyUserFilter>
          </Box>
        )}

        {!isEmployee && (
          <Box sx={{ display: 'flex', flexDirection: 'row-reverse', width: 1 }}>
            <FilterButton
              text={
                <Box display="flex" alignItems="center">
                  <StyledFilterIcon />
                </Box>
              }
              onClick={onClickFilterButton}
              ref={filtersPopoverAnchorEl}
              disabled={(isAdminOrAccountManager && !companyId) || isCopyModeEnabled}
            />
            {viewType === ContractsPageViewType.CALENDAR && (
              <FiltersMenu
                open={Boolean(filtersPopoverAnchorEl)}
                anchorEl={filtersPopoverAnchorEl}
                onClose={onCloseFilterPopover}
                onApplyFilters={onCloseFilterPopover}
                onClearFilters={onClearContractsFilters}
                filters={calendarViewFilters}
              />
            )}
            {viewType === ContractsPageViewType.LIST && listViewCurrentTab !== 3 && (
              <FiltersMenu
                open={Boolean(filtersPopoverAnchorEl)}
                anchorEl={filtersPopoverAnchorEl}
                onClose={onCloseFilterPopover}
                onApplyFilters={onCloseFilterPopover}
                onClearFilters={onClearContractsFilters}
                filters={listViewFilters}
              />
            )}
            {viewType === ContractsPageViewType.LIST && listViewCurrentTab === 3 && (
              <FiltersMenu
                open={Boolean(filtersPopoverAnchorEl)}
                anchorEl={filtersPopoverAnchorEl}
                onClose={onCloseFilterPopover}
                onApplyFilters={onCloseFilterPopover}
                onClearFilters={onClearContractsFilters}
                filters={listViewFilters}
              />
            )}
            {viewType === ContractsPageViewType.AVAILABILITY && (
              <FiltersMenu
                open={Boolean(filtersPopoverAnchorEl)}
                anchorEl={filtersPopoverAnchorEl}
                onClose={onCloseFilterPopover}
                onApplyFilters={onCloseFilterPopover}
                onClearFilters={onClearCompanyAvailabilityFilters}
                filters={availabilityViewFilters}
              />
            )}
            {viewType === ContractsPageViewType.AVAILABILITY && (
              <FilterButton
                text={
                  <Box display="flex" alignItems="center">
                    <DownloadIcon fill="white" />
                  </Box>
                }
                onClick={exportAvailabilities}
                disabled={(isAdminOrAccountManager && !companyId) || isCopyModeEnabled}
              />
            )}
          </Box>
        )}
        {!isEmployee && (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '90px' }}>
            <IconWithMargin
              sx={{
                border: viewType === ContractsPageViewType.CALENDAR ? '1px solid #008C15' : '1px solid #9EA2A2',
                display: 'flex',
                padding: '5px',
                borderRadius: '5px 0px 0px 5px',
              }}
              onClick={() => {
                setViewType(ContractsPageViewType.CALENDAR);
                setListViewType(ContractListViewTypes.Future);
                filterContracts([
                  { key: 'employeeIds', value: [] },
                  { key: 'date', value: null },
                ]);
                setSelectedEmployee('');
                setSelectedDay('');
              }}
            >
              {viewType === ContractsPageViewType.CALENDAR ? <CalendarViewIconGreen /> : <CalendarViewIconGrey />}
            </IconWithMargin>
            <IconWithMargin
              sx={{
                border: viewType === ContractsPageViewType.LIST ? '1px solid #008C15' : '1px solid #9EA2A2',
                marginLeft: '0px',
                display: 'flex',
                padding: '5px',
              }}
              onClick={() => {
                setViewType(ContractsPageViewType.LIST);
                setShowBottomGreenBar(false);
                setToBeConfirmed('');
              }}
              data-gtm-event="contracts-list-view"
            >
              {viewType === ContractsPageViewType.LIST ? <ListViewIconGreen /> : <ListViewIconGrey />}
            </IconWithMargin>
            {canViewAvailabilities() && (
              <IconWithMargin
                sx={{
                  border: viewType === ContractsPageViewType.AVAILABILITY ? '1px solid #008C15' : '1px solid #9EA2A2',
                  marginLeft: '0px',
                  display: 'flex',
                  padding: '5px',
                  borderRadius: '0px 5px 5px 0px',
                }}
                onClick={() => {
                  setViewType(ContractsPageViewType.AVAILABILITY);
                  setLocation('');
                  setContractType('');
                  setShowBottomGreenBar(false);
                  setListViewType(ContractListViewTypes.Future);
                }}
              >
                {viewType === ContractsPageViewType.AVAILABILITY ? <AvailabilityIconGreen /> : <AvailabilityIconGrey />}
              </IconWithMargin>
            )}
          </Box>
        )}
      </Box>
      {openAddModal && (
        <AddContractModal
          onClose={() => {
            setOpenAddModal(false);
            reloadContracts();
            dispatch(getCostCentersDropdownItemsAction({ companyId: toNumber(companyId) }));
          }}
          onSuccess={() => {
            setIsAdd(true);
          }}
          companyId={companyId}
          week={currentWeek}
          weekDate={week}
          defaultBreakTime={defaultBreakTime}
        />
      )}
      {openEditModal && (
        <EditContractModal
          onClose={() => {
            if (isEmployee) {
              setOpenEditModal(false);
              return;
            }
            setOpenConfirmCloseModal(true);
          }}
          onSuccess={() => {
            navigate('/contracts');
            setOpenEditModal(false);
            setOpenConfirmCloseModal(false);
            reloadContracts();
            dispatch(getCostCentersDropdownItemsAction({ companyId: toNumber(companyId) }));
          }}
          companyId={cardToEdit.companyId.toString()}
          week={currentWeek}
          weekDate={week}
          contractToEdit={cardToEdit}
          defaultBreakTime={defaultBreakTime}
          isContractSlot={cardToEdit.employee === null}
          isViewOnly={isViewOnlyMode}
        />
      )}
      {openContractSlotModal && (
        <AddContractSlotModal
          onClose={() => {
            setOpenContractSlotModal(false);
            fetchContractCards();
          }}
          week={currentWeek}
          weekDate={week}
          companyId={companyId}
          defaultBreakTime={defaultBreakTime}
        />
      )}
      {openConfirmModal && (
        <ConfirmContractModal
          open={openConfirmModal}
          onClose={() => {
            setListViewType(ContractListViewTypes.Future);
            navigate('/contracts');
            setOpenConfirmModal(false);
            fetchContractCards();
          }}
          companyId={companyId}
          contractDateId={cardToConfirmId}
          // TO DO: INCLUDE THE ERROR MESSAGE IN THE REQUEST
          // contractError={cardToConfirm.errorMessage ? cardToConfirm.errorMessage : null}
          onSuccess={() => {
            setTimeout(() => {
              fetchContractCards();
            }, 1500);
          }}
        />
      )}
      {detailPanelContractDateId !== null && (
        <ContractDetailPanel
          contractDateId={detailPanelContractDateId}
          companyId={toNumber(companyId)}
          onClose={handleDetailPanelClose}
          onSaveSuccess={() => {
            setDetailPanelContractDateId(null);
            setTimeout(() => fetchContractCards(), 1500);
          }}
          mode="confirm"
        />
      )}
      {openAlreadyConfirmed && (
        <MessageModal
          message={t(CONTRACTS__ALREADY_CONFIRMED)}
          buttonText={t(GENERAL__CANCEL_BUTTON)}
          open={openAlreadyConfirmed}
          onClose={() => setOpenAlreadyConfirmed(false)}
        />
      )}
      {openDeletePastContractModal && (
        <MessageModal
          message={t(CONTRACTS__DELETE_PAST_CONTRACT_ERROR)}
          buttonText={t(GENERAL__CANCEL_BUTTON)}
          open={openDeletePastContractModal}
          onClose={() => setOpenDeletePastContractModal(false)}
        />
      )}
      {openConfirmDeleteWeeklyModal && (
        <DeleteModal
          open={openConfirmDeleteWeeklyModal}
          content={t(CONTRACTS__DELETE_WEEKLY_CONTRACT_WARNING)}
          onClose={() => {
            setListViewType(ContractListViewTypes.Future);
            setOpenConfirmDeleteWeeklyModal(false);
            setToDeleteIds([]);
          }}
          onDelete={() => {
            makeDeleteRequest(toDeleteIds);
          }}
        />
      )}
      {openTooManyContractsModal && (
        <MessageModal
          message={t(CONTRACTS__PLEASE_USE_FILTERS)}
          buttonText={t(GENERAL__CANCEL_BUTTON)}
          open={openTooManyContractsModal}
          onClose={() => {
            setOpenTooManyContractsModal(false);
            dispatch(tooManyContractsAction(false));
          }}
        />
      )}
      {viewType === ContractsPageViewType.CALENDAR ? (
        <ContractsGridView
          copyWeekly={setWeeklyIdToCopy}
          setOpenConfirmModal={onClickConfirmCard}
          contractCards={contractCards}
          changeContractCards={setContractCards}
          onPaste={onPaste}
          days={checkedDays}
          onChangeWeek={onChangeWeek}
          onClickEditCard={onClickEditCard}
          onClickFillSlot={onClickEditCard}
          onDeleteCheckedContracts={onDeleteCheckedContracts}
          onClickReopenConfirmation={onClickReopenConfirmation}
          companyId={companyId}
          year={year}
          weekNumber={currentWeek}
          filters={createFilters()}
          getMoreContracts={fetchMoreContracts}
        />
      ) : (
        <Box />
      )}
      {viewType === ContractsPageViewType.LIST ? (
        <ContractsListView
          companyId={companyId}
          currentWeek={currentWeek}
          currentYear={year}
          setViewType={setListViewType}
          filters={createDefaultListViewFilters()}
          currentTab={listViewCurrentTab}
          setCurrentTab={setListViewCurrentTab}
          locationId={toNumber(location) || null}
          workerClass={contractType || null}
          functionId={toNumber(selectedFunction) || null}
        />
      ) : (
        <Box />
      )}
      {viewType === ContractsPageViewType.AVAILABILITY ? (
        <CompanyAvailability
          week={week}
          setWeek={setWeek}
          currentWeek={currentWeek}
          companyId={companyId}
          setYear={setYear}
          disableCreateButtons={setDisabledCreateButtons}
        />
      ) : (
        <Box />
      )}
      {showBottomGreenBar && (
        <BottomGreenBar
          numberOfItems={isCopyModeEnabled && Boolean(contractCardToCopy) ? 1 : selectedContractCardIds.length}
          isDeletionPossible={isDeletionPossible}
          actions={bottomGreenBarActions}
          onClearAll={onClearAll}
          onDelete={() => onDeleteCheckedContracts(selectedContractCardIds)}
          onCancelCopyMode={() => {
            dispatch(toggleCopyModeAction());
            dispatch(setContractCardToCopyAction(null));
          }}
          onCopy={onCopyCheckedContracts}
        />
      )}
      {showDeletedPlanningBanner && (
        <BottomErrorBar
          message={t(CONTRACTS__PLANNING_DELETED_BANNER)}
          onClose={() => setShowDeletedPlanningBanner(false)}
        />
      )}
      {openConfirmCloseModal && (
        <DeleteModal
          open={openConfirmCloseModal}
          content={t(CONTRACTS__ARE_YOU_SURE)}
          onClose={() => {
            setOpenConfirmCloseModal(false);
          }}
          onDelete={onAcceptClosingEditModal}
        />
      )}
      {openConfirmDeleteSignedModal && (
        <DeleteModal
          open={openConfirmDeleteSignedModal}
          content={contractIsSignedMessage}
          onClose={() => {
            onCloseDeleteSignedModal();
            setContractIsSignedError([]);
          }}
          onDelete={() => {
            setContractIsSignedError([]);
            makeDeleteRequest(selectedSignedContractsIds, true);
            onCloseDeleteSignedModal();
          }}
        />
      )}
      {isCopyModeEnabled && (weeklyIdToCopy || openCopyMultipleModal) && (
        <CopyMultipleModal
          open
          content={weeklyIdToCopy ? t(CONTRACTS__PASTE_WEEKLY_CONTRACT) : t(CONTRACTS__COPY_MULTIPLE_CONTRACTS)}
          onClose={onCloseCopyMultipleModal}
          items={getTheNextTwoQuartersWeeks(t(HEADER__WEEK))}
          onPaste={onPasteMultipleContracts}
          onEditBeforePaste={onEditBeforePastingWeekly}
          isForSingleWeekly={!!weeklyIdToCopy}
        />
      )}
      {openEditBeforePastingModal && weeklyContractToCopy && (
        <EditBeforePasteWrapper
          onClose={() => {
            setOpenEditBeforePastingModal(false);
            reloadContracts();
          }}
          onSuccess={null}
          companyId={companyId}
          week={weekNumberForPasting}
          weekDate={weekForPasting}
          contractToCopy={weeklyContractToCopy}
          defaultBreakTime={defaultBreakTime}
        />
      )}
      {contractsErrors.length > 0 &&
        contractsErrorWithOpen.map((err: any, index: number) => {
          const auxKey = err.type as keyof ErrorMessages;
          return (
            <UPSnackBar
              key={`err_${index.toString()}`}
              vertical="bottom"
              horizontal="right"
              open={err.open}
              content={
                err?.contractId ? `${contractsErrorMessages[auxKey]} ${err.contractId}` : contractsErrorMessages[auxKey]
              }
              type="error"
              onClose={() => handleErrorClose(err.id)}
              index={index}
            />
          );
        })}
      {contractValidationErrors && !isValidationSnackbarHidden && (
        <UPSnackBar
          vertical="bottom"
          horizontal="right"
          open={Boolean(contractValidationErrors)}
          content={`${translateValidationMessage(t, contractValidationErrors)} - ${contractValidationExtraInfo ?? ''}`}
          type="error"
          onClose={() => setIsValidationSnackbarHidden(true)}
          index={0}
        />
      )}
      {saveContractsErrors.length > 0 && (
        <UPSnackBar
          vertical="bottom"
          horizontal="right"
          open
          content={getSnackBarContent()}
          type="error"
          onClose={() => setSaveContractsErrors([])}
          index={0}
          hideDuration={saveContractsErrors.length > 1 ? 10000 : 5000}
        />
      )}
    </Box>
  );
};

export default Contracts;
